// packages/main/app/web/components-v2/UnifiedImageDialog/index.tsx
import * as Api from '@ViewModels';
import { css } from 'aphrodite';
import * as React from 'react';
import { TabContent, TabList, TabRoot, TabTrigger } from '../Tabs';
import { Checkbox } from '../Checkbox';
import { Label } from '../Label';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/Tooltip';
import { InfoIcon } from '../../containers/AutomationFlows/components/CanvasInfoPopover';
import { XIcon } from '../../components/svgs/icons/XIcon';
import { Dialog, DialogBody, DialogClose, DialogPopup } from '../Dialog';
import { AIEditTab } from './AIEditTab';
import { AIGenerateTab } from './AIGenerateTab';
import { BulkRebrandingTab } from './BulkRebrandingTab';
import { CropTab } from './CropTab';
import { FreeImagesTab } from './FreeImagesTab';
import { ImageLibraryTab } from './ImageLibraryTab';
import { LogoOverlayTab } from './LogoOverlayTab';
import { ProvidedImagesTab } from './ProvidedImagesTab';
import { SelectImageTab } from './SelectImageTab';
import { UploadTab } from './UploadTab';
import { UploadVideoTab } from './UploadVideoTab';
import { getSocialMediaPlatformLogoUrl } from '../../../models/utils/socialUtils';
import { s } from './styles';
import { TabValue, UnifiedImageDialogProps } from './types';

/** Tabs whose selection can land in the image library. Video, bulk rebranding, and select-image cannot. */
const SAVE_TO_LIBRARY_TABS = new Set<TabValue>(['upload', 'free-images', 'ai-generate', 'ai-edit']);
/**
 * Tabs whose change lands on the one version the destination banner names. The logo overlay is a
 * setting of the whole post, so the banner stays off it.
 */
const VERSION_TABS = new Set<TabValue>([
	'provided-images',
	'upload',
	'image-library',
	'select-image',
	'ai-generate',
	'ai-edit',
	'bulk-rebranding',
	'free-images',
	'upload-video',
	'crop',
]);

export function UnifiedImageDialog({
	open,
	onOpenChange,
	initialTab = 'upload',
	initialImage,
	onImageSelected,
	onVideoJobGenerated,
	onVideoAttachmentGenerated,
	impersonationContext,
	postContent,
	postContentType = 'social',
	templateId,
	showFreeImagesTab = true,
	showUploadTab = true,
	showUploadVideoTab = false,
	showImageLibraryTab = false,
	offerSaveToLibrary = false,
	addUploadsToLibrary = false,
	postId,
	getPostIdForVideoUpload,
	forUserId,
	disabledTabs = [],
	disabledTabsReason = {},
	acceptedFormats,
	resizeForSocialMedia,
	resizeForNewsletter,
	onValidateSelectedFile,
	allowMultipleUpload,
	maxUploadImages,
	onMultipleImagesSelected,
	bulkRebrandingImages,
	onBulkRebrandingImagesSelected,
	priorBrandingJobId,
	showSelectImageTab = false,
	destinationPlatform,
	logoOverlay,
	crop,
	onSelectImages,
	onPreProcessSelectImage,
	selectImageTitle,
	selectImageSubtitle,
	allowMultipleSelect = false,
	imagesForSelection,
	providedImages,
	providedImagesLabel = 'Images',
	providedImagesEmptyText = 'No images yet.',
	showAIGenerateTab = true,
	allowAIEdit = true,
}: UnifiedImageDialogProps) {
	const [selectedTab, setSelectedTab] = React.useState<TabValue>(initialTab);
	const [aiEditImage, setAiEditImage] = React.useState<Api.IFileAttachment | null>(null);
	const [saveToLibrary, setSaveToLibrary] = React.useState(false);

	// Reset only on the closed->open edge. Keying on initialTab too would clobber the user's current tab,
	// their save-to-library choice, and any in-flight AI edit whenever a parent re-rendered with a new value.
	// It happens during the opening render, not in an effect after it: the popup focuses the selected tab
	// as it opens, and a Radix tab selects itself on focus, so the tab left from the last visit would win.
	const [wasOpen, setWasOpen] = React.useState(open);
	if (open !== wasOpen) {
		setWasOpen(open);
		if (open) {
			setSelectedTab(initialTab);
			setAiEditImage(null);
			setSaveToLibrary(false);
		}
	}
	const tabsRef = React.useRef<HTMLDivElement>(null);

	// Opt-in per image, except for the library manager's own upload where saving is the point.
	const shouldSaveToLibrary = addUploadsToLibrary || saveToLibrary;
	const showSaveToLibraryOption = offerSaveToLibrary && !addUploadsToLibrary && SAVE_TO_LIBRARY_TABS.has(selectedTab);
	const showDestinationBanner = !!destinationPlatform && VERSION_TABS.has(selectedTab);

	const handleImageSelected = React.useCallback(
		(image: Api.IFileAttachment) => {
			onImageSelected(image);
			onOpenChange(false);
		},
		[onImageSelected, onOpenChange]
	);

	const handleMultipleImagesSelected = React.useCallback(
		(images: Api.IFileAttachment[]) => {
			onMultipleImagesSelected?.(images);
			onOpenChange(false);
		},
		[onMultipleImagesSelected, onOpenChange]
	);

	const handleEditWithAI = React.useCallback((image: Api.IFileAttachment) => {
		setAiEditImage(image);
		setSelectedTab('ai-edit');
	}, []);

	// Use aiEditImage if available, otherwise fall back to initialImage
	const editImage = allowAIEdit ? aiEditImage || initialImage : undefined;
	const editWithAI = allowAIEdit ? handleEditWithAI : undefined;

	const closeButton = (
		<DialogClose aria-label='Close' className={css(s.closeButton)}>
			<XIcon className={css(s.closeIcon)} />
		</DialogClose>
	);

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			{/* The popup's own close button is pinned over the content, where it covers each tab's top-right and
			    anything scrolled beneath it, so the close control gets a row of its own instead. */}
			<DialogPopup
				// A disabled tab takes no focus, so the default lands on the first disabled tab's info icon and
				// opens its tooltip unasked. The selected tab is where the tab pattern starts keyboard users.
				initialFocus={() =>
					tabsRef.current?.querySelector<HTMLElement>('[role="tab"][aria-selected="true"]:not([disabled])') ?? true
				}
				showCloseButton={false}
				styles={[s.dialogContent]}
			>
				<DialogBody styles={[s.dialogBody]}>
					<TabRoot
						className={css(s.tabsContainer)}
						ref={tabsRef}
						orientation='vertical'
						value={selectedTab}
						onValueChange={value => setSelectedTab(value as TabValue)}
					>
						<TabList styles={[s.verticalTabList]}>
							{providedImages ? (
								<TabTrigger styles={[s.tabTrigger]} value='provided-images'>
									{providedImagesLabel}
								</TabTrigger>
							) : null}
							{showUploadTab ? (
								<TabTrigger
									styles={[s.tabTrigger]}
									disabled={disabledTabs.includes('upload')}
									title={disabledTabsReason['upload']}
									value='upload'
								>
									Upload Image
									{disabledTabsReason['upload'] && (
										<Tooltip>
											<TooltipTrigger className={css(s.tooltipTrigger)}>
												<InfoIcon />
											</TooltipTrigger>
											<TooltipContent>{disabledTabsReason['upload']}</TooltipContent>
										</Tooltip>
									)}
								</TabTrigger>
							) : null}
							{showImageLibraryTab ? (
								<TabTrigger
									styles={[s.tabTrigger]}
									disabled={disabledTabs.includes('image-library')}
									title={disabledTabsReason['image-library']}
									value='image-library'
								>
									My Images
									{disabledTabsReason['image-library'] && (
										<Tooltip>
											<TooltipTrigger className={css(s.tooltipTrigger)}>
												<InfoIcon />
											</TooltipTrigger>
											<TooltipContent>{disabledTabsReason['image-library']}</TooltipContent>
										</Tooltip>
									)}
								</TabTrigger>
							) : null}
							{showSelectImageTab ? (
								<TabTrigger
									styles={[s.tabTrigger]}
									disabled={disabledTabs.includes('select-image')}
									title={disabledTabsReason['select-image']}
									value='select-image'
								>
									Select Image
									{disabledTabsReason['select-image'] && (
										<Tooltip>
											<TooltipTrigger className={css(s.tooltipTrigger)}>
												<InfoIcon />
											</TooltipTrigger>
											<TooltipContent>{disabledTabsReason['select-image']}</TooltipContent>
										</Tooltip>
									)}
								</TabTrigger>
							) : null}
							{showAIGenerateTab ? (
								<TabTrigger
									styles={[s.tabTrigger]}
									disabled={disabledTabs.includes('ai-generate')}
									title={disabledTabsReason['ai-generate']}
									value='ai-generate'
								>
									Generate with AI
									{disabledTabsReason['ai-generate'] && (
										<Tooltip>
											<TooltipTrigger className={css(s.tooltipTrigger)}>
												<InfoIcon />
											</TooltipTrigger>
											<TooltipContent>{disabledTabsReason['ai-generate']}</TooltipContent>
										</Tooltip>
									)}
								</TabTrigger>
							) : null}
							{editImage && (
								<TabTrigger
									styles={[s.tabTrigger]}
									disabled={disabledTabs.includes('ai-edit')}
									title={disabledTabsReason['ai-edit']}
									value='ai-edit'
								>
									Edit with AI
									{disabledTabsReason['ai-edit'] && (
										<Tooltip>
											<TooltipTrigger className={css(s.tooltipTrigger)}>
												<InfoIcon />
											</TooltipTrigger>
											<TooltipContent>{disabledTabsReason['ai-edit']}</TooltipContent>
										</Tooltip>
									)}
								</TabTrigger>
							)}

							{showFreeImagesTab ? (
								<TabTrigger
									styles={[s.tabTrigger]}
									disabled={disabledTabs.includes('free-images')}
									title={disabledTabsReason['free-images']}
									value='free-images'
								>
									Browse Free Images
									{disabledTabsReason['free-images'] && (
										<Tooltip>
											<TooltipTrigger className={css(s.tooltipTrigger)}>
												<InfoIcon />
											</TooltipTrigger>
											<TooltipContent>{disabledTabsReason['free-images']}</TooltipContent>
										</Tooltip>
									)}
								</TabTrigger>
							) : null}
							{showUploadVideoTab ? (
								<TabTrigger
									styles={[s.tabTrigger]}
									disabled={disabledTabs.includes('upload-video')}
									title={disabledTabsReason['upload-video']}
									value='upload-video'
								>
									Upload Video
									{disabledTabsReason['upload-video'] && (
										<Tooltip defaultOpen={false}>
											<TooltipTrigger className={css(s.tooltipTrigger)}>
												<InfoIcon />
											</TooltipTrigger>
											<TooltipContent>{disabledTabsReason['upload-video']}</TooltipContent>
										</Tooltip>
									)}
								</TabTrigger>
							) : null}

							{bulkRebrandingImages ? (
								<TabTrigger
									styles={[s.tabTrigger]}
									disabled={disabledTabs.includes('bulk-rebranding')}
									title={disabledTabsReason['bulk-rebranding']}
									value='bulk-rebranding'
								>
									Apply my Branding
									{disabledTabsReason['bulk-rebranding'] && (
										<Tooltip>
											<TooltipTrigger className={css(s.tooltipTrigger)}>
												<InfoIcon />
											</TooltipTrigger>
											<TooltipContent>{disabledTabsReason['bulk-rebranding']}</TooltipContent>
										</Tooltip>
									)}
								</TabTrigger>
							) : null}
							{crop ? (
								<TabTrigger
									styles={[s.tabTrigger]}
									disabled={disabledTabs.includes('crop')}
									title={disabledTabsReason['crop']}
									value='crop'
								>
									Crop
									{disabledTabsReason['crop'] && (
										<Tooltip>
											<TooltipTrigger className={css(s.tooltipTrigger)}>
												<InfoIcon />
											</TooltipTrigger>
											<TooltipContent>{disabledTabsReason['crop']}</TooltipContent>
										</Tooltip>
									)}
								</TabTrigger>
							) : null}
							{logoOverlay ? (
								<TabTrigger
									styles={[s.tabTrigger]}
									disabled={disabledTabs.includes('logo-overlay')}
									title={disabledTabsReason['logo-overlay']}
									value='logo-overlay'
								>
									Logo Overlay
									{disabledTabsReason['logo-overlay'] && (
										<Tooltip>
											<TooltipTrigger className={css(s.tooltipTrigger)}>
												<InfoIcon />
											</TooltipTrigger>
											<TooltipContent>{disabledTabsReason['logo-overlay']}</TooltipContent>
										</Tooltip>
									)}
								</TabTrigger>
							) : null}
						</TabList>
						<div className={css(s.tabContentWrapper)}>
							{showDestinationBanner ? (
								<div className={css(s.destinationBanner)}>
									<img
										alt=''
										className={css(s.destinationLogo)}
										src={getSocialMediaPlatformLogoUrl(destinationPlatform)}
									/>
									<span className={css(s.destinationBannerText)}>
										Working on your <strong>{destinationPlatform}</strong> version.{' '}
										{selectedTab === 'crop'
											? 'Crops you make here publish there only.'
											: 'Media you add here publishes there only.'}
									</span>
									{closeButton}
								</div>
							) : (
								<div className={css(s.closeBar)}>{closeButton}</div>
							)}
							{providedImages ? (
								<TabContent value='provided-images' className={css(s.tabContent)}>
									<ProvidedImagesTab
										images={providedImages}
										title={providedImagesLabel}
										emptyText={providedImagesEmptyText}
										onImageSelected={handleImageSelected}
									/>
								</TabContent>
							) : null}
							<TabContent value='upload' className={css(s.tabContent)}>
								<UploadTab
									onImageSelected={handleImageSelected}
									onEditWithAI={editWithAI}
									impersonationContext={impersonationContext}
									addToLibrary={shouldSaveToLibrary}
									acceptedFormats={acceptedFormats}
									resizeForSocialMedia={resizeForSocialMedia}
									resizeForNewsletter={resizeForNewsletter}
									onValidateSelectedFile={onValidateSelectedFile}
									allowMultiple={allowMultipleUpload}
									maxImages={maxUploadImages}
									onMultipleImagesSelected={handleMultipleImagesSelected}
								/>
							</TabContent>
							{showImageLibraryTab ? (
								<TabContent value='image-library' className={css(s.tabContent)}>
									<ImageLibraryTab
										onImageSelected={handleImageSelected}
										onEditWithAI={handleEditWithAI}
										impersonationContext={impersonationContext}
									/>
								</TabContent>
							) : null}
							{showSelectImageTab ? (
								<TabContent value='select-image' className={css(s.tabContent)}>
									<SelectImageTab
										imagesForSelection={imagesForSelection}
										allowMultipleSelect={allowMultipleSelect}
										onSelectImages={onSelectImages}
										onPreProcessSelectImage={onPreProcessSelectImage}
										title={selectImageTitle}
										subtitle={selectImageSubtitle}
										onOpenChange={onOpenChange}
										onEditWithAI={handleEditWithAI}
									/>
								</TabContent>
							) : null}
							{showAIGenerateTab ? (
								<TabContent value='ai-generate' className={css(s.tabContent)}>
									<AIGenerateTab
										addToLibrary={shouldSaveToLibrary}
										onImageSelected={handleImageSelected}
										impersonationContext={impersonationContext}
										postContent={postContent}
										postContentType={postContentType}
									/>
								</TabContent>
							) : null}
							{editImage && (
								<TabContent value='ai-edit' className={css(s.tabContent)}>
									<AIEditTab
										addToLibrary={shouldSaveToLibrary}
										initialImage={editImage}
										onImageSelected={handleImageSelected}
										impersonationContext={impersonationContext}
										templateId={templateId}
									/>
								</TabContent>
							)}
							<TabContent value='free-images' className={css(s.tabContent)}>
								<FreeImagesTab
									onImageSelected={handleImageSelected}
									onEditWithAI={handleEditWithAI}
									content={postContent}
									addToLibrary={shouldSaveToLibrary}
									impersonationContext={impersonationContext}
								/>
							</TabContent>
							{showUploadVideoTab && (
								<TabContent value='upload-video' className={css(s.tabContent)}>
									<UploadVideoTab
										postId={postId}
										getPostIdForVideoUpload={getPostIdForVideoUpload}
										forUserId={forUserId}
										onVideoJobGenerated={onVideoJobGenerated}
										onVideoAttachmentGenerated={onVideoAttachmentGenerated}
										onValidateSelectedFile={onValidateSelectedFile}
										impersonationContext={impersonationContext}
									/>
								</TabContent>
							)}
							{bulkRebrandingImages ? (
								<TabContent value='bulk-rebranding' className={css(s.tabContent)}>
									<BulkRebrandingTab
										bulkRebrandingImages={bulkRebrandingImages}
										impersonationContext={impersonationContext}
										onBulkRebrandingImagesSelected={onBulkRebrandingImagesSelected}
										onOpenChange={onOpenChange}
										priorBrandingJobId={priorBrandingJobId}
										templateId={templateId}
									/>
								</TabContent>
							) : null}
							{crop ? (
								<TabContent value='crop' className={css(s.tabContent)}>
									<CropTab {...crop} impersonationContext={impersonationContext} />
								</TabContent>
							) : null}
							{logoOverlay ? (
								<TabContent value='logo-overlay' className={css(s.tabContent)}>
									<LogoOverlayTab {...logoOverlay} />
								</TabContent>
							) : null}
							{showSaveToLibraryOption ? (
								<div className={css(s.saveToLibraryBar)}>
									<Checkbox
										checked={saveToLibrary}
										id='unified-image-dialog-save-to-library'
										onCheckedChange={checked => setSaveToLibrary(checked === true)}
									/>
									<Label htmlFor='unified-image-dialog-save-to-library' styles={[s.saveToLibraryLabel]}>
										Save to my images so I can reuse it later
									</Label>
								</div>
							) : null}
						</div>
					</TabRoot>
				</DialogBody>
			</DialogPopup>
		</Dialog>
	);
}
